import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography } from '@mui/material';
import { memo, useState, useCallback, useEffect } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { CertificateFormVM } from 'front-office/view-models/certificate/certificate-form';
import AccordionAccountholderForm from './form';
import { AccordionAccountholderFormVM } from 'front-office/view-models/certificate/accountholder-form';
import { useAccountHolder } from 'front-office/hooks/storage';

interface AccountholderProps {
  disabled?: boolean;
  isDeclaration?: boolean;
  isForAttestazione?: boolean;
  url?: string;
  isAuthentic?: boolean;
}

const AccountholderComponent = ({
  disabled,
  isDeclaration,
  isForAttestazione,
  url,
  isAuthentic,
}: AccountholderProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(true);

  const formContext = useFormikContext<CertificateFormVM>();

  const accountHolderContext = useAccountHolder();

  useEffect(() => {
    if (!disabled) {
      formContext.setFieldValue('accountholder', accountHolderContext.values?.accountholder);
    }
  }, []);

  useEffect(() => {
    if (!disabled) {
      accountHolderContext.setValues({
        accountholder: formContext.values.accountholder,
      });
    }
  }, [formContext.values.accountholder]);

  const onFieldAccountholderChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('accountholder', {
        ...formContext.values.accountholder,
        [key]: value,
      });
    },
    [formContext.values.accountholder, formContext.setFieldValue]
  );

  const onFieldsAccountholderChange = useCallback(
    (values) => {
      formContext.setFieldValue('accountholder', {
        ...formContext.values.accountholder,
        ...(values || {}),
      });
    },
    [formContext.values.accountholder, formContext.setFieldValue]
  );

  useEffect(() => {
    if (formContext?.values?.certificateInternOnly && !disabled && !open) {
      setOpen(true);
    }

    if (
      formContext?.values?.certificateInternOnly &&
      !disabled &&
      formContext?.values?.accountholder?.sameApplicant // entra solamente se è true e quindi viene messo a false
    ) {
      formContext.setFieldValue('accountholder', {
        ...formContext.initialValues.accountholder,
        sameApplicant: false,
      });
    }
  }, [
    formContext?.values?.certificateInternOnly,
    disabled,
    formContext?.values?.accountholder?.sameApplicant,
    open,
  ]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-certificate-accountholder-content"
        id="panel-certificate-accountholder-header"
        expandIcon={<ExpandMore />}
      >
        <Typography>Intestatario</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <AccordionAccountholderForm
          isForAttestazione={isForAttestazione}
          disabled={disabled}
          url={url}
          values={formContext.values.accountholder}
          isDeclaration={isDeclaration}
          visibilityTable={formContext.values.visibilitySearchAccountholder || false}
          setVisibilityTable={(visibility) =>
            formContext.setFieldValue('visibilitySearchAccountholder', visibility)
          }
          onChange={onFieldAccountholderChange}
          onChangeValues={onFieldsAccountholderChange}
          errors={formContext.errors.accountholder as FormikErrors<AccordionAccountholderFormVM>}
          touched={
            formContext.touched
              .accountholder as unknown as FormikTouched<AccordionAccountholderFormVM>
          }
          isAuthentic={isAuthentic}
        />
      </AccordionDetails>
    </Accordion>
  );
};

AccountholderComponent.displayName = 'Accordion accountholder component';

const Accountholder = memo(AccountholderComponent);

export default Accountholder;
